import { Button } from '@app/components/various/Button';
import { Remove, ShoppingBag } from '@app/components/various/Icons';
import { getSoldOutLabel } from '@app/ducks';
import { getProductSearchQuery } from '@app/logic/products';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { useToggleInOrder } from '@app/utils/hooks/useToggleInOrder';
import { Product } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useProductItemContext } from '../context/ProductItemContext';

interface Props {
  product: Product.Standard;
}

export const ProductLineButtons = ({ product }: Props) => {
  const { t } = useTranslation(['common', 'products', 'selections']);
  const productId = product.product;
  const { isProductAvailable } = useProductItemContext();
  const { isLoading, isRemoving, isAdding, isInOrder, toggleInOrder } = useToggleInOrder({ product });
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const soldOutLabel = useSelector(getSoldOutLabel);
  const handleTrashClick = () => {
    toggleInOrder();
  };
  const handleProductTracking = () => {
    trackEvent({ name: 'listing.button.addtoselection.click' });
  };

  const handleClick = () => {
    if (!isInOrder) {
      handleProductTracking();
      toggleInOrder();

      return;
    }

    navigate({ search: getProductSearchQuery({ productId }) });
  };

  const buttonCopy = React.useMemo(() => {
    if (!isProductAvailable) {
      return soldOutLabel;
    }
    if (isAdding) {
      return t('common:adding');
    }

    if (isRemoving) {
      return t('common:removing');
    }

    if (!isInOrder) {
      return t('selections:add_to_selection');
    }

    return t('selections:remove_from_selection');
  }, [isProductAvailable, isAdding, isRemoving, isInOrder, t, soldOutLabel]);

  const buttonIcon = React.useMemo(() => {
    if (!isProductAvailable || isAdding || isRemoving) {
      return undefined;
    }

    if (!isInOrder) {
      return ShoppingBag;
    }

    return Remove;
  }, [isAdding, isInOrder, isProductAvailable, isRemoving]);

  return (
    <div>
      <Button
        isLoading={isLoading}
        {...(isInOrder && isProductAvailable ?
          {
            color: 'secondary',
            variant: 'bordered',
          }
        : {
            color: 'primary',
            variant: 'solid',
          })}
        disabled={!isProductAvailable}
        icon={buttonIcon}
        data-testid={isInOrder ? 'removeProductButton' : 'addProductButton'}
        onClick={isInOrder ? handleTrashClick : handleClick}
      >
        {buttonCopy}
      </Button>
    </div>
  );
};
